export type WhenPreset = 'anytime' | 'today' | 'weekend' | 'next7days' | 'thisMonth' | 'custom';

/**
 * Child-age mode. 'child' = a specific age is set. Adult-only moved to the
 * participation filter (docs/prd-homepage-redesign.md §4.3).
 */
export type AudienceMode = 'any' | 'child';

/** Stage age range from a homepage deep link: [lo, hi); hi undefined = no upper bound. */
export interface AgeRangeFilter {
  lo: number;
  hi?: number;
}

export interface FilterState {
  whenPreset: WhenPreset;
  dateFrom: string; // YYYY-MM-DD (PT-local), empty = unset
  dateTo: string;
  audience: AudienceMode;
  age: string; // input as string to allow empty; only meaningful when audience='child'
  ageRange: AgeRangeFilter | null;
  regions: string[];
  categories: string[];
  languages: string[];
  participations: string[];
}

export const EMPTY_FILTERS: FilterState = {
  whenPreset: 'anytime',
  dateFrom: '',
  dateTo: '',
  audience: 'any',
  age: '',
  ageRange: null,
  regions: [],
  categories: [],
  languages: [],
  participations: [],
};

export const ADULT_ONLY = 'ADULT_ONLY';

export function isAgeActive(f: FilterState): boolean {
  return f.audience === 'child' && f.age.trim() !== '';
}

export function isFilterActive(f: FilterState): boolean {
  return (
    f.whenPreset !== 'anytime' ||
    f.dateFrom !== '' ||
    f.dateTo !== '' ||
    isAgeActive(f) ||
    f.ageRange !== null ||
    f.regions.length > 0 ||
    f.categories.length > 0 ||
    f.languages.length > 0 ||
    f.participations.length > 0
  );
}

export function activeFilterCount(f: FilterState): number {
  let n = 0;
  if (f.whenPreset !== 'anytime') n++;
  if (isAgeActive(f) || f.ageRange !== null) n++;
  if (f.regions.length > 0) n++;
  if (f.categories.length > 0) n++;
  if (f.languages.length > 0) n++;
  if (f.participations.length > 0) n++;
  return n;
}

/**
 * Setting a specific age replaces a stage range (PRD S7) and drops adult-only,
 * which cannot match a child's age.
 */
export function withAge(f: FilterState, age: string): FilterState {
  if (age === '') return { ...f, age: '', audience: 'any' };
  return {
    ...f,
    age,
    audience: 'child',
    ageRange: null,
    participations: f.participations.filter((p) => p !== ADULT_ONLY),
  };
}

/** Choosing adult-only clears any child age or stage range, like the old Adult toggle. */
export function withParticipationToggled(f: FilterState, p: string): FilterState {
  const on = !f.participations.includes(p);
  const participations = on ? [...f.participations, p] : f.participations.filter((x) => x !== p);
  if (on && p === ADULT_ONLY) {
    return { ...f, participations, age: '', audience: 'any', ageRange: null };
  }
  return { ...f, participations };
}

export function toggled(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

/** A child's age in half years, 0 to 99 (the same range as activity ages). */
export const MAX_AGE = 99;

const clampAge = (n: number) => Math.max(0, Math.min(MAX_AGE, Math.round(n * 2) / 2));
const formatAge = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/** Normalizes an age from the URL or the box: nearest half year, 0..99, '' when absent. */
export function sanitizeAge(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return '';
  return formatAge(clampAge(num));
}

/**
 * What the age box accepts while typing: up to two digits, optionally followed by
 * ".5" (or the "." on the way there). Anything else is ignored, so nothing arbitrary
 * can be typed.
 */
export function acceptTypedAge(raw: string): string | null {
  if (!/^\d{0,2}(\.5?)?$/.test(raw)) return null;
  return raw.replace(/^0+(?=\d)/, '');
}

/** A finished value the filter can use: '' or a whole or half year (not "1." mid-typing). */
export function isCompleteAge(text: string): boolean {
  return text === '' || /^\d{1,2}(\.5)?$/.test(text);
}

/** Next age after a half-year step, starting from 5 when empty, kept within 0..99. */
export function stepAge(current: string, delta: number): string {
  const n = current === '' ? 5 : Number(current);
  return formatAge(clampAge((Number.isFinite(n) ? n : 5) + delta));
}
